import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaPause, FaStop, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { getSignedUrlForS3Path, isProperlySignedS3Url } from '../../utils/s3PathUtils';

/**
 * S3AudioPreview — play / pause / stop for one audio object in the Database Audit views.
 *
 * ⭐ THE POINT IS THE UNKNOWNS. The S3 Impact list names files that no longer have a healthy owner
 * in the graph, and a filename alone cannot tell an admin whether an orphan is a discarded take, a
 * finished render worth keeping, or silence. Hearing four seconds of it can. This exists to make a
 * reclaim decision an informed one rather than a guess from a UUID.
 *
 * ⭐ AND A FILE THAT WILL NOT PLAY IS ALSO AN ANSWER. If signing fails or the object is gone, this
 * says so on the row instead of failing quietly — "the DB still references something S3 no longer
 * has" is exactly the kind of thing this screen exists to surface. So errors are rendered, not
 * swallowed.
 */

/**
 * Module-level, so only ONE preview plays at a time across the whole list.
 *
 * Without this, clicking through a dozen unknowns layers them into noise and tells the admin
 * nothing. Starting any preview stops whichever was running.
 */
let currentlyPlaying = null;
const stopCurrent = () => {
  if (currentlyPlaying && typeof currentlyPlaying.stop === 'function') currentlyPlaying.stop();
  currentlyPlaying = null;
};

const S3AudioPreview = ({ url, userId }) => {
  const [state, setState] = useState('idle');   // idle | loading | playing | paused | error
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const signedRef = useRef(null);

  /**
   * Ask S3 directly what went wrong.
   *
   * ⭐ `MEDIA_ERR_SRC_NOT_SUPPORTED` ("no supported source was found") is the browser's catch-all:
   * it means "I fetched that and could not use it", and it reads the same for a 404, a 403, a bad
   * signature and a genuinely unplayable file. On this screen those are completely different
   * answers — "the object is gone, reclaim is safe" versus "the object is there and our signing is
   * wrong" — so the element's own message is nearly useless and worth replacing with the status.
   *
   * ⚠️ A RANGED GET, not HEAD. A presigned URL's signature covers the HTTP METHOD, so a HEAD
   * against a GET-signed URL is rejected as a signature mismatch — which would invent a failure
   * and report it as the cause. `Range: bytes=0-0` fetches one byte with the method that was
   * actually signed.
   */
  const probeReason = useCallback(async (signedUrl) => {
    try {
      const r = await fetch(signedUrl, { method: 'GET', headers: { Range: 'bytes=0-0' } });
      // ⚠️ S3 answers 403 for a key that does not exist when the caller lacks s3:ListBucket, so
      // "forbidden" here usually means "not there" — say both rather than pick one.
      if (r.status === 403) return 'HTTP 403 — object missing, or the signature was rejected';
      if (r.status === 404) return 'HTTP 404 — not in the bucket';
      if (!r.ok) return `HTTP ${r.status}`;
      const ct = r.headers.get('content-type') || 'unknown type';
      return `fetched OK (${ct}) — the browser cannot decode it`;
    } catch (_) {
      // fetch() enforces CORS where a media element does not, so this can fail on a file that
      // would otherwise play. Suggested, not asserted.
      return 'request blocked — possibly bucket CORS';
    }
  }, []);

  const teardown = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
  }, []);

  // Release the element on unmount — a details block collapsing mid-playback must not leave audio
  // running with no visible control to stop it.
  useEffect(() => () => {
    if (currentlyPlaying && currentlyPlaying.id === audioRef) currentlyPlaying = null;
    const a = audioRef.current;
    if (a) { a.pause(); a.src = ''; }
  }, []);

  const play = async () => {
    setError(null);

    // Resume rather than re-sign: pausing and continuing must not cost a round trip.
    if (state === 'paused' && audioRef.current) {
      stopCurrent();
      currentlyPlaying = { id: audioRef, stop: () => { teardown(); setState('idle'); } };
      try {
        await audioRef.current.play();
        setState('playing');
      } catch (e) {
        setError('Playback blocked'); setState('error');
      }
      return;
    }

    // ⚠️ Reported separately because it fails EVERY row at once, and a per-row "cannot play" would
    // read as "these files are gone" when the real answer is that nothing was ever asked for.
    if (!userId) {
      setError('No signed-in user — cannot sign');
      setState('error');
      return;
    }

    setState('loading');
    try {
      // ⭐ SIGNED LAZILY, ON FIRST PLAY. Signing every row when the list expands would fire a burst
      // of requests for files nobody listens to, and signed URLs expire — so the useful moment to
      // ask for one is the moment someone wants to hear it.
      if (!signedRef.current) {
        const signed = await getSignedUrlForS3Path(url, userId);

        // ⛔ getSignedUrlForS3Path RETURNS THE ORIGINAL URL WHEN SIGNING FAILS — it never throws.
        // Left undetected, a signing failure reaches the <audio> element as an unsigned key, the
        // browser cannot load it, and it surfaces as "file missing" — blaming the object for a
        // problem in the request. These are opposite findings on this screen: one says reclaim is
        // safe, the other says the tooling is broken. Tell them apart here.
        if (!signed || signed === url || !isProperlySignedS3Url(signed)) {
          setError('Could not sign — check the key');
          setState('error');
          return;
        }
        signedRef.current = signed;
      }

      stopCurrent();

      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = 'none';
        audioRef.current.onended = () => { setState('idle'); currentlyPlaying = null; };
        audioRef.current.onerror = async () => {
          // The object is referenced but unfetchable — deleted, moved, or the key is wrong. That
          // is a finding, not a glitch, so name the actual status rather than repeating the
          // element's catch-all.
          setState('error');
          currentlyPlaying = null;
          setError('Cannot play — checking…');
          setError(`Cannot play — ${await probeReason(signedRef.current)}`);
        };
      }
      if (audioRef.current.src !== signedRef.current) {
        audioRef.current.src = signedRef.current;
      }

      currentlyPlaying = { id: audioRef, stop: () => { teardown(); setState('idle'); } };
      await audioRef.current.play();
      setState('playing');
    } catch (e) {
      // play() rejects with the same unhelpful "no supported source was found" the error event
      // gives. Signing failures are caught above, so ask S3 what it actually said.
      setState('error');
      setError('Cannot play — checking…');
      setError(signedRef.current
        ? `Cannot play — ${await probeReason(signedRef.current)}`
        : (e?.message || 'Cannot play'));
    }
  };

  const pause = () => {
    if (audioRef.current) audioRef.current.pause();
    setState('paused');
    currentlyPlaying = null;
  };

  const stop = () => {
    teardown();
    setState('idle');
    currentlyPlaying = null;
  };

  // ⭐ SQUARE, AND FLEX-CENTRED. An inline <svg> sits on the TEXT BASELINE, so it renders high in
  // the box with the line's descender space left empty beneath it — the arrow reads as stuck to
  // the top. Asymmetric padding (`1px 6px`) made the box wider than tall on top of that.
  //
  // Fixed width == height with padding:0 gives a square; inline-flex + centring puts the glyph in
  // the middle of it on both axes, which baseline alignment cannot do. lineHeight:1 stops the
  // inherited line box from re-introducing the vertical offset.
  const btn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    padding: 0,
    background: 'none',
    border: '1px solid #ccc',
    borderRadius: 3,
    cursor: 'pointer',
    marginLeft: 4,
    lineHeight: 1,
    fontSize: '0.85em',
    verticalAlign: 'middle'
  };

  return (
    <span
      className="s3-audio-preview"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: 8,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle'
      }}
    >
      {state === 'playing' ? (
        <button type="button" style={btn} onClick={pause} title="Pause"><FaPause /></button>
      ) : (
        <button
          type="button"
          style={btn}
          onClick={play}
          disabled={state === 'loading'}
          title={state === 'paused' ? 'Resume' : 'Play'}
        >
          {/* `spin` is animated by database-audit.css, scoped to .s3-audio-preview. FontAwesome's
              own `fa-spin` is not available — this project ships react-icons, not FA's CSS. */}
          {state === 'loading' ? <FaSpinner className="spin" /> : <FaPlay />}
        </button>
      )}

      {/* Stop is offered only when there is something to stop — a dead control invites a click
          that does nothing and teaches the admin to distrust the row. */}
      {(state === 'playing' || state === 'paused') && (
        <button type="button" style={btn} onClick={stop} title="Stop"><FaStop /></button>
      )}

      {error && (
        <span style={{ marginLeft: 6, color: '#b32424', fontSize: '0.85em' }} title={url}>
          <FaExclamationTriangle /> {error}
        </span>
      )}
    </span>
  );
};

export default S3AudioPreview;
